import { useEffect, useState, useCallback, useMemo } from 'react';
import { spriteApi, SpriteEntry } from '../../../api/adminApi';
import { useTranslation } from '../../../i18n/useTranslation';
import { ToastContainer, useToast } from '../../../components/Toast/Toast';
import SpriteCalibrator, { type CalibratorValue } from './SpriteCalibrator';
import '../admin.css';

const EMPTY_CALIB: CalibratorValue = { rowOrder: [], frameOverrides: {} };

// Deve acompanhar client_max_body_size/post_max_size dos nginx e do php.ini
// (docker/nginx/conf/{default.conf,host-proxy.conf.example}, docker/php/php.ini).
const MAX_SPRITE_UPLOAD_MB = 30;

// ── Confirm Modal ─────────────────────────────────────────────────────────────

function ConfirmModal({ message, onConfirm, onCancel }: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 14, padding: '28px 32px', minWidth: 320, maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        animation: 'toast-enter 0.25s ease',
      }}>
        <p style={{ color: '#e8eaf6', fontSize: 15, marginBottom: 24, lineHeight: 1.5 }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btnSecondary" onClick={onCancel}>
            Cancelar
          </button>
          <button
            className="btn btnPrimary"
            style={{ background: 'var(--error, #ff4d6a)', borderColor: 'var(--error, #ff4d6a)' }}
            onClick={onConfirm}
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function SpriteManagerPage() {
  const { t } = useTranslation();
  const { messages, toast, remove } = useToast();
  const [sprites, setSprites] = useState<SpriteEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(Date.now());

  // Form states
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [icon, setIcon] = useState('🐉');
  const [type, setType] = useState<'class' | 'monster' | 'npc'>('monster');
  const [cols, setCols] = useState(3);
  const [rows, setRows] = useState(8);
  const [scale, setScale] = useState(1.0);
  const [offsetX, setOffsetX] = useState<number | string>(0);
  const [offsetY, setOffsetY] = useState<number | string>(0);
  const [file, setFile] = useState<File | null>(null);
  // Muda a cada reset pra forçar o <input type="file"> (não-controlado) a
  // esquecer o arquivo escolhido — setFile(null) sozinho não limpa o input.
  const [fileInputKey, setFileInputKey] = useState(0);
  // Calibração por posição (rowOrder + frameOverrides)
  const [calib, setCalib] = useState<CalibratorValue>(EMPTY_CALIB);
  // Sprite em edição (pra montar a URL da imagem quando não há arquivo novo)
  const [editingSprite, setEditingSprite] = useState<SpriteEntry | null>(null);

  // URL da imagem pro calibrador: arquivo escolhido agora, senão o do sprite editado.
  const calibImageUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    if (editingSprite) return `/sprites/${editingSprite.filename || editingSprite.name + '.png'}?v=${refreshKey}`;
    return null;
  }, [file, editingSprite, refreshKey]);
  useEffect(() => {
    if (file && calibImageUrl?.startsWith('blob:')) {
      return () => URL.revokeObjectURL(calibImageUrl);
    }
  }, [file, calibImageUrl]);

  const resetForm = useCallback(() => {
    setEditingId(null);
    setEditingSprite(null);
    setName('');
    setLabel('');
    setIcon('🐉');
    setType('monster');
    setCols(3);
    setRows(8);
    setScale(1.0);
    setOffsetX(0);
    setOffsetY(0);
    setFile(null);
    setCalib(EMPTY_CALIB);
    setFileInputKey(k => k + 1);
  }, []);

  // ── Search + pagination for the "Available Sprites" list ──
  const SPRITES_PER_PAGE = 50;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const fetchSprites = useCallback(async () => {
    try {
      const data = await spriteApi.list();
      setSprites(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSprites(); }, [fetchSprites]);

  // Nome "de sistema" seguro — espelha AdminSpriteController::normalizeName no
  // backend: espaço (e qualquer caractere fora de [A-Za-z0-9_-]) vira "_",
  // "_" repetidos colapsam, pontas limpas. "image (2)" → "image_2".
  const sanitizeName = (raw: string) =>
    raw.replace(/[^A-Za-z0-9_-]+/g, '_').replace(/_+/g, '_').replace(/^[-_]+|[-_]+$/g, '');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const picked = e.target.files[0];
      const maxBytes = MAX_SPRITE_UPLOAD_MB * 1024 * 1024;
      if (picked.size > maxBytes) {
        toast.error(
          t('sprite.alertFileTooLarge')
            .replace('{size}', (picked.size / (1024 * 1024)).toFixed(1))
            .replace('{limit}', String(MAX_SPRITE_UPLOAD_MB))
        );
        setFileInputKey(k => k + 1); // limpa o <input> — não deixa o arquivo inválido preso
        return;
      }
      setFile(picked);
      // Sugere um nome limpo a partir do arquivo (remove QUALQUER extensão e
      // normaliza) quando o campo ainda está vazio.
      if (!name) {
        setName(sanitizeName(picked.name.replace(/\.[^.]+$/, '')));
      }
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !label || !icon || (!file && !editingId)) {
      toast.error(t('sprite.alertFillFields'));
      return;
    }
    if (file && file.size > MAX_SPRITE_UPLOAD_MB * 1024 * 1024) {
      toast.error(
        t('sprite.alertFileTooLarge')
          .replace('{size}', (file.size / (1024 * 1024)).toFixed(1))
          .replace('{limit}', String(MAX_SPRITE_UPLOAD_MB))
      );
      return;
    }

    const formData = new FormData();
    formData.append('name', name);
    formData.append('label', label);
    formData.append('icon', icon);
    formData.append('type', type);
    formData.append('cols', cols.toString());
    formData.append('rows', rows.toString());
    formData.append('scale', scale.toString());
    formData.append('offsetX', offsetX.toString());
    formData.append('offsetY', offsetY.toString());
    formData.append('rowOrder', JSON.stringify(calib.rowOrder));
    formData.append('frameOverrides', JSON.stringify(calib.frameOverrides));
    if (file) {
      formData.append('file', file);
    }

    try {
      setLoading(true);
      await spriteApi.create(formData);
      toast.success(t('sprite.alertSuccess'));
      resetForm();
      setRefreshKey(Date.now());
      fetchSprites();
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error && err.message ? err.message : t('sprite.alertErrorUp'));
    } finally {
      setLoading(false);
    }
  };

  // Trocar duas posições por arrastar-e-soltar no calibrador já persiste na
  // hora (sprite existente) — sem passar pelo botão "Guardar Alterações" e
  // sem resetar o formulário, pra continuar editando no mesmo lugar.
  const persistSwap = useCallback(async (nextCalib: CalibratorValue) => {
    if (!editingId) {
      toast.info('Troca aplicada — clique em "Guardar Alterações" pra salvar (sprite ainda não existe).');
      return;
    }
    const formData = new FormData();
    formData.append('name', name);
    formData.append('label', label);
    formData.append('icon', icon);
    formData.append('type', type);
    formData.append('cols', cols.toString());
    formData.append('rows', rows.toString());
    formData.append('scale', scale.toString());
    formData.append('offsetX', offsetX.toString());
    formData.append('offsetY', offsetY.toString());
    formData.append('rowOrder', JSON.stringify(nextCalib.rowOrder));
    formData.append('frameOverrides', JSON.stringify(nextCalib.frameOverrides));
    try {
      await spriteApi.create(formData);
      toast.success('Posições trocadas e salvas.');
      fetchSprites();
    } catch (err) {
      console.error(err);
      toast.error('Não deu pra salvar a troca — tenta de novo.');
    }
  }, [editingId, name, label, icon, type, cols, rows, scale, offsetX, offsetY, fetchSprites, toast]);

  const handleDeleteConfirmed = async () => {
    if (!confirmId) return;
    const id = confirmId;
    setConfirmId(null);
    try {
      setLoading(true);
      await spriteApi.delete(id);
      toast.success('Sprite eliminado com sucesso.');
      fetchSprites();
    } catch (err: any) {
      toast.error(err.message || t('sprite.alertErrorDel'));
      setLoading(false);
    }
  };

  const PREVIEW = 72;

  const typeLabel = (tp: string) =>
    tp === 'monster' ? t('sprite.assignMonster')
    : tp === 'npc'   ? t('sprite.assignNpc')
    :                  t('sprite.assignClass');

  // Filter by free-text search (name / label / type), then paginate.
  const q = search.trim().toLowerCase();
  const filteredSprites = q
    ? sprites.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.label.toLowerCase().includes(q) ||
        s.type.toLowerCase().includes(q) ||
        typeLabel(s.type).toLowerCase().includes(q))
    : sprites;

  const pageCount = Math.max(1, Math.ceil(filteredSprites.length / SPRITES_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pagedSprites = filteredSprites.slice(
    safePage * SPRITES_PER_PAGE,
    safePage * SPRITES_PER_PAGE + SPRITES_PER_PAGE,
  );

  return (
    <>
      {/* ── Toast Notifications ── */}
      <ToastContainer messages={messages} onRemove={remove} />

      {/* ── Confirm Modal ── */}
      {confirmId && (
        <ConfirmModal
          message={t('sprite.confirmDelete')}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setConfirmId(null)}
        />
      )}

      <div className="adminHeader">
        <h1 style={{ marginBottom: 20 }}>{t('sprite.title')}</h1>
      </div>
      <div className="adminContent">

        {/* ── Specs ── */}
        <div className="adminCard" style={{ marginBottom: 30 }}>
          <div className="adminCardHeader">{t('sprite.specTitle')}</div>
          <div style={{ padding: '15px' }}>
            <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 8 }}>
              {t('sprite.specDesc')}
            </p>
            <ul style={{ fontSize: 13, marginTop: 10, lineHeight: 1.6, background: 'var(--bg-dark)', padding: 15, borderRadius: 8 }}>
              <li>{t('sprite.specFormat')}</li>
              <li>{t('sprite.specSize')}</li>
              <li>{t('sprite.specFrame')}</li>
              <li>{t('sprite.specCols')}</li>
              <li>{t('sprite.specRows')}</li>
            </ul>

            {/* ── Outros tipos de asset visual: sem upload aqui, apenas referência ── */}
            <h4 style={{ marginTop: 20, marginBottom: 4, fontSize: 14 }}>{t('sprite.specItemsTitle' as any)}</h4>
            <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 4 }}>
              {t('sprite.specItemsDesc' as any)}
            </p>
            <ul style={{ fontSize: 13, marginTop: 10, lineHeight: 1.6, background: 'var(--bg-dark)', padding: 15, borderRadius: 8 }}>
              <li>{t('sprite.specItemsFormat' as any)}</li>
              <li>{t('sprite.specItemsSize' as any)}</li>
              <li>{t('sprite.specItemsLocation' as any)}</li>
            </ul>

            <h4 style={{ marginTop: 20, marginBottom: 4, fontSize: 14 }}>{t('sprite.specPropsTitle' as any)}</h4>
            <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 4 }}>
              {t('sprite.specPropsDesc' as any)}
            </p>
            <ul style={{ fontSize: 13, marginTop: 10, lineHeight: 1.6, background: 'var(--bg-dark)', padding: 15, borderRadius: 8 }}>
              <li>{t('sprite.specPropsFormat' as any)}</li>
              <li>{t('sprite.specPropsSize' as any)}</li>
              <li>{t('sprite.specPropsLocation' as any)}</li>
            </ul>
          </div>
        </div>

        {/* ── Upload Form ── */}
        <div className="adminCard" style={{ marginBottom: 30 }}>
          <div className="adminCardHeader">
            {editingId ? 'Editar Sprite' : t('sprite.uploadTitle')}
            {editingId && (
              <button 
                type="button" 
                className="btn btnSecondary" 
                style={{ marginLeft: 'auto', padding: '4px 8px', fontSize: 12 }}
                onClick={resetForm}
              >
                Cancelar Edição
              </button>
            )}
          </div>
          <form onSubmit={handleUpload} className="adminForm" style={{ padding: '15px' }}>
            <div className="formGrid">
              <div className="formGroup">
                <label className="formLabel">{t('sprite.fileLabel')} {editingId ? '(Opcional)' : ''}</label>
                <input key={fileInputKey} type="file" accept=".png" className="formInput" onChange={handleFileChange} required={!editingId} />
              </div>
              <div className="formGroup">
                <label className="formLabel">{t('sprite.nameLabel')}</label>
                <input type="text" className="formInput" value={name}
                  onChange={e => setName(sanitizeName(e.target.value))} required
                  placeholder={t('sprite.namePlaceholder')} 
                  readOnly={!!editingId} 
                  style={editingId ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>

            <div className="formGrid">
              <div className="formGroup">
                <label className="formLabel">{t('sprite.displayLabel')}</label>
                <input type="text" className="formInput" value={label}
                  onChange={e => setLabel(e.target.value)} required
                  placeholder={t('sprite.labelPlaceholder')} />
              </div>
              <div className="formGroup">
                <label className="formLabel">{t('sprite.iconLabel')}</label>
                <input type="text" className="formInput" value={icon}
                  onChange={e => setIcon(e.target.value)} required
                  placeholder={t('sprite.iconPlaceholder')} />
              </div>
              <div className="formGroup">
                <label className="formLabel">{t('sprite.assignLabel')}</label>
                <select className="formSelect" value={type}
                  onChange={e => setType(e.target.value as 'class' | 'monster' | 'npc')}>
                  <option value="monster">{t('sprite.assignMonster')}</option>
                  <option value="class">{t('sprite.assignClass')}</option>
                  <option value="npc">{t('sprite.assignNpc')}</option>
                </select>
              </div>
            </div>

            <div className="formGrid" style={{ marginTop: 15 }}>
              <div className="formGroup">
                <label className="formLabel">Colunas (Frames por linha)</label>
                <input type="number" className="formInput" value={cols} min={1} max={30}
                  onChange={e => setCols(parseInt(e.target.value) || 3)} required />
              </div>
              <div className="formGroup">
                <label className="formLabel">Linhas (Direções/Ações)</label>
                <input type="number" className="formInput" value={rows} min={1} max={30}
                  onChange={e => setRows(parseInt(e.target.value) || 8)} required />
              </div>
              <div className="formGroup">
                <label className="formLabel">Escala Visual (ex: 1.0)</label>
                <input type="number" className="formInput" value={scale} step={0.1} min={0.1} max={5}
                  onChange={e => setScale(parseFloat(e.target.value) || 1.0)} required />
              </div>
              <div className="formGroup">
                <label className="formLabel">Desvio X (OffsetX)</label>
                <input type="number" className="formInput" value={offsetX}
                  onChange={e => setOffsetX(e.target.value)} required />
              </div>
              <div className="formGroup">
                <label className="formLabel">Desvio Y (OffsetY)</label>
                <input type="number" className="formInput" value={offsetY}
                  onChange={e => setOffsetY(e.target.value)} required />
              </div>
            </div>

            {/* ── Calibração por posição + preview "como fica no jogo" ── */}
            <div style={{ marginTop: 20, padding: 15, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-dark)' }}>
              <SpriteCalibrator
                imageUrl={calibImageUrl}
                cols={cols}
                rows={rows}
                scale={scale}
                offsetX={typeof offsetX === 'string' ? (parseFloat(offsetX) || 0) : offsetX}
                offsetY={typeof offsetY === 'string' ? (parseFloat(offsetY) || 0) : offsetY}
                value={calib}
                onChange={setCalib}
                onSwapped={persistSwap}
              />
            </div>

            <button type="submit" className="btn btnPrimary" disabled={loading || !name || !label || !icon || (!editingId && !file)}
              style={{ alignSelf: 'flex-start', marginTop: 15, opacity: (!name || !label || !icon || (!editingId && !file)) ? 0.5 : 1 }}>
              {loading ? (editingId ? 'A guardar...' : t('sprite.btnUploading')) : (editingId ? 'Guardar Alterações' : t('sprite.btnUpload'))}
            </button>
          </form>
        </div>

        {/* ── Sprite Grid ── */}
        <div className="adminCard">
          <div className="adminCardHeader">{t('sprite.listTitle')}</div>
          <div style={{ padding: '15px' }}>

            {/* Search */}
            <div style={{ marginBottom: 15 }}>
              <input
                type="text"
                className="formInput"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                placeholder={t('sprite.searchPlaceholder')}
                style={{ width: '100%', maxWidth: 420 }}
              />
            </div>

            {loading && sprites.length === 0 ? <p>{t('common.loading')}</p> : (
              filteredSprites.length === 0 ? (
                <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>{t('sprite.searchNoResults')}</p>
              ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 15 }}>
                {pagedSprites.map(s => (
                  <div key={s.id} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    padding: '12px', borderRadius: 8, border: '1px solid var(--border)',
                    background: 'var(--surface)', minWidth: 120, position: 'relative'
                  }}>
                    <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', marginBottom: 4 }}>
                      <button type="button" onClick={() => {
                        setEditingId(s.id!);
                        setEditingSprite(s);
                        setName(s.name);
                        setLabel(s.label);
                        setIcon(s.icon);
                        setType(s.type);
                        setCols(s.cols ?? 3);
                        setRows(s.rows ?? 8);
                        setScale(s.scale ?? 1.0);
                        setOffsetX(s.offsetX ?? 0);
                        setOffsetY(s.offsetY ?? 0);
                        setCalib({
                          rowOrder: Array.isArray(s.rowOrder) ? s.rowOrder : [],
                          frameOverrides: s.frameOverrides && typeof s.frameOverrides === 'object' ? s.frameOverrides : {},
                        });
                        setFile(null);
                        setFileInputKey(k => k + 1);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        toast.success(`A editar o sprite: ${s.label}`);
                      }} style={{
                        background: 'transparent', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: '4px', padding: '2px 8px',
                        cursor: 'pointer', fontSize: 12, fontWeight: 600
                      }} title="Editar">Editar</button>
                      
                      <button type="button" onClick={() => setConfirmId(s.id!)} style={{
                        background: 'transparent', color: 'var(--error)', border: '1px solid var(--error)', borderRadius: '4px', padding: '2px 8px',
                        cursor: 'pointer', fontSize: 12, fontWeight: 600
                      }} title="Eliminar">Remover</button>
                    </div>

                    <div style={{ width: PREVIEW, height: PREVIEW, overflow: 'hidden', position: 'relative', borderRadius: 4, background: 'var(--input-bg)' }}>
                      <img src={`/sprites/${s.filename || s.name + '.png'}?v=${refreshKey}`} alt={s.label}
                        style={{ 
                          position: 'absolute', 
                          top: s.offsetY || 0, 
                          left: s.offsetX || 0, 
                          width: `calc(${PREVIEW}px * ${s.cols || 3})`, 
                          height: 'auto', 
                          imageRendering: 'pixelated',
                          transform: `scale(${s.scale || 1})`,
                          transformOrigin: 'top left'
                        }} />
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600, marginTop: 4 }}>{s.icon} {s.label}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase' }}>
                      {typeLabel(s.type)}
                    </span>
                  </div>
                ))}
              </div>
              )
            )}

            {/* Pagination */}
            {filteredSprites.length > 0 && pageCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 20 }}>
                <button
                  type="button"
                  className="btn btnSecondary"
                  disabled={safePage <= 0}
                  onClick={() => setPage(Math.max(0, safePage - 1))}
                >
                  ‹ {t('sprite.pagePrev')}
                </button>
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                  {t('sprite.pageInfo')
                    .replace('{current}', String(safePage + 1))
                    .replace('{total}', String(pageCount))
                    .replace('{count}', String(filteredSprites.length))}
                </span>
                <button
                  type="button"
                  className="btn btnSecondary"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
                >
                  {t('sprite.pageNext')} ›
                </button>
              </div>
            )}
          </div>
        </div>

      </div>
    </>
  );
}
